// ─────────────────────────────────────────────────────────────────────────────
// Aviso de cuánto espacio le queda al mensaje de un reconocimiento, compartido por las cuatro
// superficies donde se escribe: el formulario clásico, el post-it interactivo, la nominación de
// campaña y la edición posterior al envío.
//
// El criterio es preventivo y no punitivo: la caja de texto está topada en `MENSAJE_MAX_LENGTH`, así
// que el usuario nunca puede pasarse; lo que hace falta es que vea venir el límite. Nada se muestra
// mientras sobra espacio, aparece el conteo de restantes en la franja final y, al topar, un texto
// que dice qué hacer ("resume alguna parte") en vez de un error por algo que ya no puede evitar.
//
// También traduce el rechazo del backend cuando de todas formas llega. Las reglas de
// `CreateRecognitionCommandValidator` declaran un código estable (bug UAT #113) y aquí se mapea a la
// clave i18n correspondiente: sin esto, la pantalla mostraba el `title` genérico del 400 —«Error de
// validación en los datos enviados», en español y sin decir qué campo— para cinco causas distintas,
// dos de ellas ajenas al formulario (sesión sin `oid`, tenant sin resolver).
// ─────────────────────────────────────────────────────────────────────────────
import { useTranslation } from 'react-i18next';
import {
  MENSAJE_MAX_LENGTH,
  debeAvisarLimite,
  mensajeRestantes,
  getRecognitionErrorCode,
  getRecognitionErrorMessage,
} from '@/api/recognition';

interface MessageLimitHintProps {
  /** Largo del mensaje REAL que se enviará (en el post-it incluye la frase armada, no solo la caja). */
  readonly length: number;
  /** Tope aplicable; se parametriza solo para pruebas y para una pantalla con un tope propio. */
  readonly max?: number;
  /**
   * `true` en el post-it interactivo: aclara que la frase armada con el banco de palabras también
   * ocupa espacio. Sin esa nota, el conteo parece no cuadrar con lo que el usuario ve tecleado.
   */
  readonly hasBuilderSentence?: boolean;
}

/**
 * Aviso de límite del mensaje. No renderiza nada hasta entrar en la franja final
 * (`MENSAJE_WARN_REMAINING` caracteres antes del tope).
 */
export function MessageLimitHint({
  length,
  max = MENSAJE_MAX_LENGTH,
  hasBuilderSentence = false,
}: MessageLimitHintProps) {
  const { t } = useTranslation('recognition');
  if (!debeAvisarLimite(length, max)) return null;

  const remaining = mensajeRestantes(length, max);
  const isAtLimit = remaining === 0;

  return (
    <p className={`rp-note rp-chars-note ${isAtLimit ? 'rp-chars-note--full' : ''}`} role="status">
      <i className={`fas ${isAtLimit ? 'fa-circle-info' : 'fa-pen-to-square'}`} />
      {' '}
      {isAtLimit
        ? t('panel.charsLimitReachedHint', { max })
        : t('panel.charsAvailableHint', { remaining, max })}
      {hasBuilderSentence && <span className="rp-chars-note-extra">{t('panel.charsLimitSentenceNote')}</span>}
    </p>
  );
}

/**
 * Clave i18n por código de rechazo del alta de reconocimiento. Los declaran
 * `CreateRecognitionCommandValidator` con `WithErrorCode` (400) y, desde el 16-sep-2026, también las
 * reglas de dominio que pasan un `code` a `RecognitionDomainException` (422). Un código nuevo que
 * este front todavía no mapee cae al texto del backend en vez de pintar una clave cruda.
 *
 * Emisor y tenant comparten mensaje a propósito: para quien envía, ambos son «la sesión no se pudo
 * identificar» y no hay nada que corregir en el formulario. La distinción (token sin `oid` frente a
 * token sin `tid`) sigue viviendo en el log del backend, que es donde sirve.
 */
const RECOGNITION_ERROR_CODE_KEYS: Readonly<Record<string, string>> = {
  'recognition.messageTooLong': 'panel.errMessageTooLong',
  'recognition.recipientRequired': 'panel.errRecipientRequired',
  'recognition.fundamentalRequired': 'panel.errFundamentalRequired',
  'recognition.senderUnknown': 'panel.errSessionUnknown',
  'recognition.tenantUnknown': 'panel.errSessionUnknown',
  // Una sola clave para uno y para varios destinatarios: el plural lo elige i18next con `count`,
  // aplicando las reglas de CADA idioma. Dos claves separadas obligarían al backend a decidir el
  // plural del español por todos, que es justo lo que hacía antes de traducirse.
  'recognition.recipientWithoutCampaign': 'panel.errRecipientWithoutCampaign',
  // Función 1 «A nombre de» (DelegationGuard en RecognitionService): 403 sin el permiso, 422 por emisor no
  // elegible y 503 si no se pudo comprobar al emisor. Familia excluida y staff comparten «no es operativo».
  'recognition.delegateNotAllowed': 'panel.errDelegateNotAllowed',
  'recognition.delegateOtherUnit': 'panel.errDelegateOtherUnit',
  'recognition.delegateNotOperational': 'panel.errDelegateNotOperational',
  'recognition.delegateNotFound': 'panel.errDelegateNotFound',
  'recognition.delegateRequesterWithoutUnit': 'panel.errDelegateRequesterWithoutUnit',
  'recognition.delegateNotEligible': 'panel.errDelegateNotEligible',
  'recognition.delegateCheckUnavailable': 'panel.errDelegateCheckUnavailable',
};

/**
 * Los parámetros del backend viajan como texto (el contrato de `errors` es `string[]`), pero
 * i18next necesita que `count` sea NÚMERO: es lo que le pasa a `Intl.PluralRules` para elegir entre
 * `_one` y `_other`. Con la cadena `'2'` la selección de plural no es de fiar, así que se convierte
 * aquí, en el único punto por el que pasan todos los códigos.
 *
 * Se convierte solo si el valor es realmente numérico: un `count` que no lo fuera quedaría como
 * `NaN` y se llevaría por delante la interpolación del texto.
 */
function conCountNumerico(
  params: Record<string, string>,
): Record<string, string | number> {
  const { count, ...resto } = params;
  if (count === undefined) return params;
  const numero = Number(count);
  return Number.isFinite(numero) ? { ...resto, count: numero } : params;
}

/**
 * Mensaje para un rechazo al enviar un reconocimiento: la traducción del código si el backend lo
 * mandó y lo conocemos, y si no el texto del backend (que viene en español) como último recurso.
 * Mismo patrón que `campaignErrorMessage` en `AdminCampaigns.tsx`.
 */
export function recognitionSendErrorMessage(
  error: unknown,
  translate: (key: string, options?: Record<string, string | number>) => string,
  fallback: string,
): string {
  const coded = getRecognitionErrorCode(error);
  const key = coded ? RECOGNITION_ERROR_CODE_KEYS[coded.code] : undefined;
  if (key && coded) return translate(key, { ...conCountNumerico(coded.params), max: MENSAJE_MAX_LENGTH });
  return getRecognitionErrorMessage(error, fallback);
}
